import React, { useMemo } from "react";
import { Tooltip } from "primereact/tooltip";
import { ComponentTheme } from "components/fragments/dashboard/components/group-component/GroupComponentConfiguratorNew";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import {
  AggregationPeriod,
  DiffType,
  DiffValueOptions,
} from "./ValueChangeIndicatorTypes";
import { formatNumber, getSortTimestamp, getTextColor } from "../charts/utils";
import { formatGroupKey, includeUpTo } from "./ValueChangeIndicatorUtils";
import "./HorizontalBarChart.scss";

interface HorizontalBarChartProps {
  data: DatasetDataInfoResponse;
  aggregationPeriod: AggregationPeriod;
  diffType: DiffType;
  theme?: ComponentTheme;
  reportMode?: boolean;
  decimalPlaces?: number;
  upToUnit?: AggregationPeriod;
  referenceTime?: string;
  setCurrentDiffValueOptions: (diffValueOptions: DiffValueOptions) => void;
}

const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const bars = useMemo(() => {
    const grouped = new Map<string, { value: number; sortKey: number }>();
    const upTo = (props.upToUnit ?? AggregationPeriod.MONTHS).toUpperCase() as AggregationPeriod;

    (props.data.data ?? []).forEach(point => {
      if (!includeUpTo(point.key, upTo, props.referenceTime)) return;

      const date = new Date(point.key);
      const groupKey = formatGroupKey(date, props.aggregationPeriod);
      const sortKey = getSortTimestamp(date, props.aggregationPeriod);
      if (grouped.has(groupKey)) {
        grouped.get(groupKey)!.value += point.value;
      } else {
        grouped.set(groupKey, { value: point.value, sortKey });
      }
    });

    const sortedBars = [...grouped.entries()]
      .sort(([, a], [, b]) => a.sortKey - b.sortKey)
      .map(([label, { value }]) => ({ label, value }));
    if (sortedBars.length >= 2) {
      const first = sortedBars[0];
      const last = sortedBars[sortedBars.length - 1];
      const diffValue =
        props.diffType === DiffType.PERCENTAGE
          ? ((last.value - first.value) / first.value) * 100
          : last.value - first.value;

      props.setCurrentDiffValueOptions({
        value: diffValue,
        currentValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{last.label}:</b>
            <div>
              {formatNumber(last.value, props.decimalPlaces)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
        previousValueInfo: (
          <div className="flex flex-row gap-2">
            <b>{first.label}:</b>
            <div>
              {formatNumber(first.value, props.decimalPlaces)}
              {props.data.unit ? ` ${props.data.unit}` : ""}
            </div>
          </div>
        ),
        isHorizontalBarChartDiffLoaded: true,
      });
    } else {
      props.setCurrentDiffValueOptions({
        value: 0,
        currentValueInfo: <>–</>,
        previousValueInfo: <>–</>,
        isHorizontalBarChartDiffLoaded: true,
      });
    }

    return sortedBars;
  }, [
    props.data,
    props.aggregationPeriod,
    props.diffType,
    props.upToUnit,
    props.referenceTime,
    props.decimalPlaces,
    props.data.unit,
    props.setCurrentDiffValueOptions,
  ]);

  const maxValue = Math.max(...bars.map(b => b.value), 0);

  return (
    <div className={`bar-chart-container${props.reportMode ? "-report-mode" : ""}`}>
      {bars.map((bar, i) => {
        const percentage = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;
        const targetId = `bar-fill-${i}`;

        return (
          <div key={bar.label} className="bar-row">
            <Tooltip target={`#${targetId}`} />
            <div className="bar-wrapper">
              <div className="bar-track">
                <div
                  id={targetId}
                  className="bar-fill"
                  style={{ width: `${percentage}%` }}
                  data-pr-tooltip={bar.label}
                  data-pr-position="top"
                >
                  <span className="bar-label-inside">{bar.label}</span>
                </div>
              </div>

              <div className="bar-value" style={{ color: getTextColor(props.theme) }}>
                <div>{formatNumber(bar.value, props.decimalPlaces)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalBarChart;
